import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { availabilityService } from '../services/availabilityService';
import {
  AddAvailabilityRequest,
  UpdateAvailabilityRequest,
  UpdateAvailabilityTimesRequest,
} from '../types/availability';

export const useAvailabilities = (apartmentId: string) => {
  return useQuery({
    queryKey: ['availabilities', apartmentId],
    queryFn: () => availabilityService.getAvailabilities(apartmentId),
    enabled: !!apartmentId,
  });
};

export const useAvailableSlots = (apartmentId: string) => {
  return useQuery({
    queryKey: ['availabilities', apartmentId, 'available'],
    queryFn: () => availabilityService.getAvailableSlots(apartmentId),
    enabled: !!apartmentId,
  });
};

export const useAddAvailability = (apartmentId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AddAvailabilityRequest) =>
      availabilityService.addAvailability(apartmentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['availabilities', apartmentId] });
    },
  });
};

export const useUpdateAvailability = (apartmentId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      availabilityId,
      data,
    }: {
      availabilityId: string;
      data: UpdateAvailabilityRequest;
    }) => availabilityService.updateAvailability(apartmentId, availabilityId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['availabilities', apartmentId] });
    },
  });
};

export const useRemoveAvailability = (apartmentId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (availabilityId: string) =>
      availabilityService.removeAvailability(apartmentId, availabilityId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['availabilities', apartmentId] });
    },
  });
};

export const useBlockAvailability = (apartmentId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (availabilityId: string) =>
      availabilityService.blockAvailability(apartmentId, availabilityId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['availabilities', apartmentId] });
    },
  });
};

export const useUnblockAvailability = (apartmentId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (availabilityId: string) =>
      availabilityService.unblockAvailability(apartmentId, availabilityId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['availabilities', apartmentId] });
    },
  });
};

// ── 022-checkin-checkout-times ───────────────────────────────────────────────

/**
 * Mutation: update default check-in/out times for an availability period.
 * On success, invalidates the availabilities cache so the list and
 * calendar view both reflect the new times.
 */
export const useUpdateAvailabilityTimes = (apartmentId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      availabilityId,
      data,
    }: {
      availabilityId: string;
      data: UpdateAvailabilityTimesRequest;
    }) => availabilityService.updateAvailabilityTimes(apartmentId, availabilityId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['availabilities', apartmentId] });
    },
  });
};

// ── end 022-checkin-checkout-times ───────────────────────────────────────────

